import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { AudienceFilter } from '../types';

interface CreateBroadcastParams {
  content: string;
  audienceFilter: AudienceFilter;
}

interface CreateBroadcastResponse {
  success: boolean;
  data: {
    broadcast_id: string;
    recipient_count: number;
  };
  errors?: string[];
}

export function useCreateBroadcast() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async ({ content, audienceFilter }: CreateBroadcastParams) => {
      const trimmed = content.trim();
      if (!trimmed) throw new Error('Broadcast content is empty');

      setCreating(true);
      setError(null);
      try {
        const res = await axiosRubyClient.post<CreateBroadcastResponse>(
          '/api/admin/broadcasts',
          {
            content: trimmed,
            message_type: 'broadcast',
            audience_filter: audienceFilter,
          },
        );
        return res.data.data;
      } catch (err: unknown) {
        const message =
          (
            err as { response?: { data?: { errors?: string[] } } }
          )?.response?.data?.errors?.join(', ') ??
          (err as Error)?.message ??
          'Failed to create broadcast';
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'create_broadcast' },
        });
        setError(message);
        throw err;
      } finally {
        setCreating(false);
      }
    },
    [],
  );

  return { create, creating, error };
}
